import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import DiscountSelectionModal from 'components/DiscountSelectionModal';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import EventSelectionModal from 'components/EventSelectionModal';
import { PhoneInput } from 'components/FormFields';
import StepHeader from 'components/StepHeader';
import Toaster from 'components/Toaster';
import { AppContext } from 'shared/AppContext';
import GET_ACCOUNT_MESSAGE_CREDITS from '../../gql/get-account-message-credits';
import CREATE_CUSTOMER_MESSAGE from '../../gql/mutations/create-customer-message';
import { default as SMS_TEST } from '../../gql/mutations/sms-test';
import GET_MESSAGE_AUDIENCE_ESTIMATE from '../../gql/queries/get-message-audience-estimate';
import MessageComposer from '../MessageComposer';
import {
  ContentGrid,
  LeftColumn,
  RightColumn,
} from '../MessageComposer/StyledComponents';
import MessagePreview from '../MessagePreview';
import CampaignGuideDrawer from './CampaignGuideDrawer';
import QuickVariablesDrawer from './QuickVariablesDrawer';
import {
  PageContainer,
  PageContent,
  StatsBar,
  StatItem,
  StatValue,
  SectionTitle,
  FooterButtons,
  NextButton,
  ReviewSection,
  ReviewLeftColumn,
  ReviewRightColumn,
  DetailCard,
  ScheduleToggle,
  ToggleLabel,
  ToggleSwitch,
  FinalPreview,
  ReadyStatus,
  StatusText,
  SendButton,
  PageTitle,
} from './StyledComponents';

const SendCampaign = ({
  segmentData: propSegmentData,
  onBack: propOnBack,
  onSuccess: propOnSuccess,
  accountId: propAccountId,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { state } = useContext(AppContext);
  const theme = useTheme();

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Get segment data from location state or props (for backward compatibility)
  const segmentData = location.state?.segmentData || propSegmentData;
  const returnTo = location.state?.returnTo || `${routePrefix}/users`;

  // Use accountId from location state, props, or context
  const effectiveAccountId =
    location.state?.accountId ||
    propAccountId ||
    state?.account?.id ||
    parseInt(localStorage.getItem('selected_account_id'));

  // Check AppContext for theme as fallback
  const isDarkTheme = theme?.name === 'dark' || state?.theme === 'dark';
  const [currentStep, setCurrentStep] = useState(2); // Start at step 2 since segment is pre-selected
  const [selectedChannel, setSelectedChannel] = useState('SMS');
  const [message, setMessage] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  // const [selectedPurpose] = useState('Event Reminder'); // Currently unused but may be needed for future features
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pendingEventVariable, setPendingEventVariable] = useState(null); // Track which variable to insert
  const [showVariableEducation, setShowVariableEducation] = useState(false);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [attachments, setAttachments] = useState([]);

  // Fetch real credit data
  const { data: creditData } = useQuery(GET_ACCOUNT_MESSAGE_CREDITS, {
    variables: { accountId: effectiveAccountId },
    skip: !effectiveAccountId,
    pollInterval: 30000, // Refresh every 30 seconds
  });

  // Use real credit data if available, otherwise fallback to defaults
  const creditBalance = creditData?.getAccountMessageCredits?.remaining || 1000;
  const creditLimit = creditData?.getAccountMessageCredits?.limit || 10000;
  const creditsUsed = creditData?.getAccountMessageCredits?.used || 0;
  const creditResetDate =
    creditData?.getAccountMessageCredits?.resetDate || 'next month';

  // Build filters object for audience estimate
  const audienceFilters = React.useMemo(() => {
    const customerFilters = {};

    // Add search term if present
    if (segmentData?.searchTerm) {
      customerFilters.search = segmentData.searchTerm;
    }

    // Add segment filter if present
    if (segmentData?.filter || segmentData?.appliedFilters?.segment) {
      customerFilters.segment =
        segmentData.filter || segmentData.appliedFilters.segment;
    }

    // Add other filters from appliedFilters
    if (segmentData?.appliedFilters) {
      // eslint-disable-next-line no-unused-vars
      const { segment, ...otherFilters } = segmentData.appliedFilters;

      // Add filters that have values
      if (otherFilters.minValue)
        customerFilters.minValue = parseFloat(otherFilters.minValue);
      if (otherFilters.maxValue)
        customerFilters.maxValue = parseFloat(otherFilters.maxValue);
      if (otherFilters.attendanceRate)
        customerFilters.attendanceRate = otherFilters.attendanceRate;
      if (otherFilters.locationCategory)
        customerFilters.locationCategory = otherFilters.locationCategory;
      if (otherFilters.dateFrom)
        customerFilters.dateFrom = otherFilters.dateFrom;
      if (otherFilters.dateTo) customerFilters.dateTo = otherFilters.dateTo;
    }

    // Build the input object matching GetMessageAudienceEstimate schema
    const input = {
      account_id: effectiveAccountId,
      channel: selectedChannel.toLowerCase(), // 'sms' or 'push'
    };

    // Only add filters if we have any
    if (Object.keys(customerFilters).length > 0) {
      input.filters = customerFilters;
    }

    return input;
  }, [effectiveAccountId, segmentData, selectedChannel]);

  // Fetch actual audience count (filters out users who opted out of notifications)
  const { data: audienceData, loading: audienceLoading } = useQuery(
    GET_MESSAGE_AUDIENCE_ESTIMATE,
    {
      variables: { input: audienceFilters },
      skip: !effectiveAccountId,
    }
  );

  // State for tracking character count data from MessageComposer
  const [characterData, setCharacterData] = useState({
    charactersUsed: 0,
    characterLimit: 160,
    segments: 1, // Default to 1 segment for initial display
    isUnicodeMessage: false,
    creditMultiplier: 1,
    hasAttachments: false,
  });

  // Store reference to insertVariableAtCursor function from MessageComposer
  const insertVariableAtCursorRef = useRef(null);

  // Scroll to top when component mounts or step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Function to replace variables with actual values for preview
  const replaceVariables = (text) => {
    if (!text) return text;

    // Get track name from context or localStorage fallback
    let trackName = 'Your Track'; // Default fallback

    // First try to get from context
    if (state && state.account) {
      if (state.account.business_dba) {
        trackName = state.account.business_dba;
      } else if (state.account.business_name) {
        trackName = state.account.business_name;
      }
    } else {
      // Fallback to localStorage if context doesn't have it
      try {
        const accountData = localStorage.getItem('selected_account');
        if (accountData) {
          const account = JSON.parse(accountData);
          if (account.business_dba) {
            trackName = account.business_dba;
          } else if (account.business_name) {
            trackName = account.business_name;
          }
        }
      } catch (error) {
        // Error getting track name
      }
    }

    // Get event details from selected event
    let eventName = 'Sprint Car Championship';
    let eventDate = 'Saturday';

    if (selectedEvent) {
      eventName = selectedEvent.name;
      // Format the date nicely
      const date = new Date(selectedEvent.start_date);
      eventDate = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    }

    // Replace variables with actual values
    let replacedText = text;
    replacedText = replacedText.replace(/\{\{first_name\}\}/g, 'John');
    replacedText = replacedText.replace(/\{\{track_name\}\}/g, trackName);
    replacedText = replacedText.replace(/\{\{event_name\}\}/g, eventName);
    replacedText = replacedText.replace(/\{\{event_date\}\}/g, eventDate);
    replacedText = replacedText.replace(
      /\{\{discount_code\}\}/g,
      selectedDiscount?.name || 'SAVE20'
    );

    // Replace attachment template variables with actual URLs for preview
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment, index) => {
        if (attachment.url) {
          const templateVar = `{{attachment_${index + 1}_url}}`;
          replacedText = replacedText.replace(
            new RegExp(templateVar.replace(/[{}]/g, '\\$&'), 'g'),
            attachment.url
          );
        }
      });
    }

    return replacedText;
  };

  const handleBack = () => {
    if (currentStep === 2) {
      // Step 2: Go back to previous page
      if (propOnBack) {
        propOnBack();
      } else {
        history.push(returnTo);
      }
    } else if (currentStep === 3) {
      // Step 3: Go back to step 2
      setCurrentStep(2);
    }
  };

  const handleNext = () => {
    if (currentStep === 2) {
      // Validate that message is not empty
      if (!message || message.trim() === '') {
        setToasterData({
          title: 'Message Required',
          message:
            'Please compose a message before continuing to the review step.',
          type: 'error',
        });
        return;
      }
      // Validate that there are recipients
      if (audienceCount === 0) {
        setToasterData({
          title: 'No Valid Recipients',
          message: `All customers in this segment have disabled ${selectedChannel} notifications. Please go back to select a different customer segment.`,
          type: 'error',
        });
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleSendTest = async () => {
    if (!message || !testPhoneNumber) {
      return;
    }

    // Clean phone number - remove all non-numeric characters
    const cleanPhone = testPhoneNumber.replace(/\D/g, '');

    // Combine country code with phone number
    const fullPhoneNumber = selectedCountry + cleanPhone;

    // Basic validation based on country code
    const minLengths = {
      '+1': 10, // US/Canada
      '+44': 10, // UK
      '+61': 9, // Australia
      '+49': 10, // Germany
      '+33': 9, // France
      '+81': 10, // Japan
      '+86': 11, // China
      '+91': 10, // India
      '+52': 10, // Mexico
      '+55': 10, // Brazil
      '+39': 10, // Italy
      '+34': 9, // Spain
      '+31': 9, // Netherlands
      '+82': 10, // South Korea
    };

    const expectedLength = minLengths[selectedCountry] || 10;
    if (cleanPhone.length !== expectedLength) {
      setToasterData({
        title: 'Invalid Phone Number',
        message: `Please enter a valid ${expectedLength}-digit phone number for the selected country`,
        type: 'error',
      });
      return;
    }

    try {
      // Build template context with sample data and actual values
      const templateContext = {
        first_name: 'John', // Sample name for test
      };

      // Add track name
      if (state && state.account) {
        if (state.account.business_dba) {
          templateContext.track_name = state.account.business_dba;
        } else if (state.account.business_name) {
          templateContext.track_name = state.account.business_name;
        }
      }

      // Add event details if selected
      if (selectedEvent) {
        templateContext.event_name = selectedEvent.name;
        const eventDate = new Date(selectedEvent.start_date);
        templateContext.event_date = eventDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        });
      }

      // Add discount code if selected
      if (selectedDiscount?.name) {
        templateContext.discount_code = selectedDiscount.name;
      }

      // Add attachment URLs if present
      if (attachments && attachments.length > 0) {
        attachments.forEach((attachment, index) => {
          if (attachment.url) {
            templateContext[`attachment_${index + 1}_url`] = attachment.url;
          }
        });
      }

      // Send the test message
      const { data } = await smsTest({
        variables: {
          input: {
            message: message,
            templateContext: templateContext,
            cellphone: fullPhoneNumber,
          },
        },
      });

      if (data?.smsTest) {
        setToasterData({
          title: 'Success!',
          message: 'Test message sent to your phone',
          type: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to send test message:', error);
      setToasterData({
        title: 'Failed to Send',
        message:
          error.message || 'Could not send test message. Please try again.',
        type: 'error',
      });
    }
  };

  // Initialize the mutations
  const [createCustomerMessage] = useMutation(CREATE_CUSTOMER_MESSAGE);
  const [smsTest, { loading: sendingTest }] = useMutation(SMS_TEST);

  const [isSending, setIsSending] = useState(false);
  const [toasterData, setToasterData] = useState(null);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('+1');

  const handleSend = async () => {
    setIsSending(true);

    try {
      // Build filters object from segmentData
      const filters = {};

      // Add search term if present
      if (segmentData?.searchTerm) {
        filters.search = segmentData.searchTerm;
      }

      // Add segment filter if present
      if (segmentData?.filter || segmentData?.appliedFilters?.segment) {
        filters.segment =
          segmentData.filter || segmentData.appliedFilters.segment;
      }

      // Add other filters from appliedFilters
      if (segmentData?.appliedFilters) {
        // eslint-disable-next-line no-unused-vars
        const { segment, ...otherFilters } = segmentData.appliedFilters;

        // Add filters that have values
        if (otherFilters.minValue)
          filters.minValue = parseFloat(otherFilters.minValue);
        if (otherFilters.maxValue)
          filters.maxValue = parseFloat(otherFilters.maxValue);
        if (otherFilters.attendanceRate)
          filters.attendanceRate = otherFilters.attendanceRate;
        if (otherFilters.locationCategory)
          filters.locationCategory = otherFilters.locationCategory;
        if (otherFilters.dateFrom) filters.dateFrom = otherFilters.dateFrom;
        if (otherFilters.dateTo) filters.dateTo = otherFilters.dateTo;
      }

      // Get track name for template context
      let trackName = 'Your Track';
      if (state?.account?.business_dba) {
        trackName = state.account.business_dba;
      } else if (state?.account?.business_name) {
        trackName = state.account.business_name;
      } else {
        // Fallback to localStorage
        const accountData = localStorage.getItem('account');
        if (accountData) {
          try {
            const account = JSON.parse(accountData);
            trackName =
              account.business_dba || account.business_name || 'Your Track';
          } catch (e) {
            console.error('Error parsing account data:', e);
          }
        }
      }

      // Prepare mutation input - send message with template variables intact
      const input = {
        account_id: parseInt(effectiveAccountId),
        channel: selectedChannel.toLowerCase(), // 'sms' or 'push'
        message: message, // Send the message with template variables intact for backend processing
        audienceCount: audienceCount,
      };

      // Include template context data for backend to use when replacing static variables
      // These are values known at campaign creation time
      const templateContext = {};

      // Add track name if available
      if (trackName && trackName !== 'Your Track') {
        templateContext.track_name = trackName;
      }

      // Add event details if available
      if (selectedEvent) {
        templateContext.event_name = selectedEvent.name;
        const eventDate = new Date(selectedEvent.start_date);
        templateContext.event_date = eventDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        });
      }

      // Add discount code if selected
      if (selectedDiscount?.name) {
        templateContext.discount_code = selectedDiscount.name;
      }

      // Add attachment URLs to templateContext if present
      if (attachments && attachments.length > 0) {
        attachments.forEach((attachment, index) => {
          if (attachment.url) {
            templateContext[`attachment_${index + 1}_url`] = attachment.url;
          }
        });
      }

      // Only add templateContext if we have values
      if (Object.keys(templateContext).length > 0) {
        input.templateContext = templateContext;
      }

      // Add filters only if we have any
      if (Object.keys(filters).length > 0) {
        input.filters = filters;
      }

      // Add push title if channel is push - keep template variables intact
      if (selectedChannel.toLowerCase() === 'push' && pushTitle) {
        input.pushTitle = pushTitle; // Keep template variables intact for backend processing
      }

      // Add scheduled date if scheduling (convert to ISO 8601 string)
      if (scheduleForLater && scheduledDateTime) {
        // Convert Date object to ISO 8601 string (includes timezone info)
        input.scheduledDateTime = scheduledDateTime.toISOString();
      }

      // NOTE: We no longer send attachments as MMS - they are now URLs in the message text

      // Execute the mutation
      const { data } = await createCustomerMessage({
        variables: { input },
      });

      // Handle success
      if (data?.createCustomerMessage?.id) {
        if (propOnSuccess) {
          propOnSuccess({
            messageId: data.createCustomerMessage.id,
            audienceCount:
              data.createCustomerMessage.audienceCount || audienceCount,
            creditsUsed:
              data.createCustomerMessage.creditsRequired || creditsNeeded,
            channel: selectedChannel,
            scheduled: scheduleForLater,
            scheduledDateTime: data.createCustomerMessage.scheduledAt,
          });
        }

        // Store success message in sessionStorage for the return page to display
        const finalAudienceCount =
          data.createCustomerMessage.audienceCount || audienceCount;
        let successMessage = `Successfully sent ${selectedChannel} to ${finalAudienceCount} customer${
          finalAudienceCount !== 1 ? 's' : ''
        }`;

        if (scheduleForLater && data.createCustomerMessage.scheduledAt) {
          const date = new Date(data.createCustomerMessage.scheduledAt);
          const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          });
          successMessage = `${selectedChannel} scheduled for ${formattedDate} at ${formattedTime}`;
        }

        sessionStorage.setItem('campaignSuccessMessage', successMessage);
        sessionStorage.setItem(
          'campaignMessageId',
          data.createCustomerMessage.id
        );
        sessionStorage.setItem(
          'campaignScheduled',
          scheduleForLater.toString()
        );

        history.push(returnTo);
      }
    } catch (error) {
      // Show error toaster while staying on the review step
      setToasterData({
        title: 'Failed to Send',
        message:
          error.message || 'Unable to send your message. Please try again.',
        type: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Audience and credit calculations
  // Use audience estimate from query (filters out users who opted out)
  const audienceCount =
    audienceData?.getMessageAudienceEstimate?.audienceCount ?? 0;

  // Calculate credits needed using data from MessageComposer
  const creditsNeeded =
    selectedChannel === 'Push'
      ? 0
      : characterData.segments * audienceCount * characterData.creditMultiplier;

  // Define campaign-specific variables with modal triggers
  const campaignVariables = [
    {
      label: 'First Name',
      value: '{{first_name}}',
      icon: 'M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    },
    {
      label: 'Event',
      value: '{{event_name}}',
      onClick: () => {
        if (selectedEvent) {
          if (insertVariableAtCursorRef.current) {
            insertVariableAtCursorRef.current('{{event_name}}');
          }
        } else {
          setPendingEventVariable('{{event_name}}');
          setIsEventModalOpen(true);
        }
      },
      icon: 'M14 3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h12zM2 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H2z',
    },
    {
      label: 'Date',
      value: '{{event_date}}',
      onClick: () => {
        if (selectedEvent) {
          if (insertVariableAtCursorRef.current) {
            insertVariableAtCursorRef.current('{{event_date}}');
          }
        } else {
          setPendingEventVariable('{{event_date}}');
          setIsEventModalOpen(true);
        }
      },
      icon: 'M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zM3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z',
    },
    {
      label: 'Discount',
      value: '{{discount_code}}',
      onClick: () => {
        if (selectedDiscount) {
          if (insertVariableAtCursorRef.current) {
            insertVariableAtCursorRef.current('{{discount_code}}');
          }
        } else {
          setIsDiscountModalOpen(true);
        }
      },
      icon: 'M3 2.5a2.5 2.5 0 0 1 5 0 2.5 2.5 0 0 1 5 0v.006c0 .07 0 .27-.038.494H15a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 14.5V7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h2.038A2.968 2.968 0 0 1 3 2.506V2.5zm1.068.5H7v-.5a1.5 1.5 0 1 0-3 0c0 .085.002.274.045.43a.522.522 0 0 0 .023.07zM9 3h2.932a.56.56 0 0 0 .023-.07c.043-.156.045-.345.045-.43a1.5 1.5 0 0 0-3 0V3zM1 4v2h6V4H1zm8 0v2h6V4H9zm5 3H9v8h4.5a.5.5 0 0 0 .5-.5V7zm-7 8V7H2v7.5a.5.5 0 0 0 .5.5H7z',
    },
  ];

  return (
    <EnhancedThemeProvider>
      <style>
        {`
          .test-message-button:hover:not(:disabled) {
            background: rgba(250, 70, 22, 0.1) !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
          .test-message-button:active:not(:disabled) {
            transform: translateY(0) !important;
          }
        `}
      </style>
      <PageContainer>
        <PageContent>
          <StepHeader
            currentStep={currentStep}
            steps={['Audience', 'Message', 'Review']}
            backPath={returnTo}
            backLabel="Back"
            onBack={handleBack}
          />
          {currentStep === 2 && (
            <>
              <PageTitle>
                Craft Your Message
                <button
                  type="button"
                  style={{
                    marginLeft: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                  onClick={() => setIsHelpOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsHelpOpen(true);
                    }
                  }}
                  aria-label="Open help panel"
                >
                  <svg
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
                  </svg>
                </button>
              </PageTitle>

              {/* Warning Banner when no recipients can receive messages */}
              {audienceCount === 0 && !audienceLoading && (
                <div
                  style={{
                    padding: '20px 24px',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${theme.colors.error}`,
                    background: isDarkTheme
                      ? 'rgba(237, 67, 55, 0.1)'
                      : 'rgba(237, 67, 55, 0.08)',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      style={{ width: '24px', height: '24px', flexShrink: 0 }}
                    >
                      <svg
                        width="24"
                        height="24"
                        fill={theme.colors.error}
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                        <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                      </svg>
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: isDarkTheme ? '#FFFFFF' : '#0F172A',
                      }}
                    >
                      Cannot Send Message - No Valid Recipients
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.6,
                      marginBottom: '16px',
                      color: isDarkTheme ? '#CBD5E1' : '#64748B',
                    }}
                  >
                    All customers in this segment (
                    {segmentData?.count || 'your selection'}) have disabled{' '}
                    {selectedChannel} notifications. Your message cannot be
                    delivered to anyone in the current selection.
                  </div>
                  <div>
                    <ButtonV2 variant="pill" onClick={handleBack}>
                      Adjust Filters
                    </ButtonV2>
                  </div>
                </div>
              )}

              <ContentGrid>
                <LeftColumn>
                  {/* Target Audience Section */}
                  <div
                    style={{
                      background: isDarkTheme
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#ffffff',
                      borderRadius: '12px',
                      padding: '20px',
                      border: `1px solid ${
                        isDarkTheme
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.08)'
                      }`,
                      boxShadow: isDarkTheme
                        ? 'rgba(0, 0, 0, 0.1) 0px 1px 3px'
                        : 'rgba(0, 0, 0, 0.05) 0px 1px 3px',
                    }}
                  >
                    <SectionTitle>
                      <svg
                        width="16"
                        height="16"
                        fill={theme?.colors?.primary || '#FA4616'}
                        viewBox="0 0 16 16"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM2.04 4.326c.325 1.329 2.532 2.54 3.717 3.19.48.263.793.434.743.484-.08.08-.162.158-.242.234-.416.396-.787.749-.758 1.266.035.634.618.824 1.214 1.017.577.188 1.168.38 1.286.983.082.417-.075.988-.22 1.52-.215.782-.406 1.48.22 1.48 1.5-.5 3.798-3.186 4-5 .138-1.243-2-2-3.5-2.5-.478-.16-.755.081-.99.284-.172.15-.322.279-.51.216-.445-.148-2.5-2-1.5-2.5.78-.39.952-.171 1.227.182.078.099.163.208.273.318.609.304.662-.132.723-.633.039-.322.081-.671.277-.867.434-.434 1.265-.791 2.028-1.12.712-.306 1.365-.587 1.579-.88A7 7 0 1 1 2.04 4.327z" />
                      </svg>
                      Target Audience
                      <div
                        style={{
                          marginLeft: 'auto',
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '4px',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: theme?.colors?.primary || '#FA4616',
                          }}
                        >
                          {audienceCount}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            color: 'rgb(148, 163, 184)',
                          }}
                        >
                          Receipients
                        </span>
                      </div>
                    </SectionTitle>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginTop: '8px',
                        padding: '0 12px',
                      }}
                    >
                      {(segmentData?.isCustomFilter ||
                        segmentData?.searchTerm) && (
                        <>
                          <div
                            style={{
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              color: 'rgb(100, 116, 139)',
                              letterSpacing: '0.05em',
                              fontWeight: 600,
                            }}
                          >
                            Active Filters
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px',
                            }}
                          >
                            {segmentData?.searchTerm && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                  style={{ marginRight: '6px' }}
                                >
                                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                                </svg>
                                Search: "{segmentData.searchTerm}"
                              </div>
                            )}
                            {segmentData?.appliedFilters?.attendanceRate && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                {segmentData.appliedFilters.attendanceRate ===
                                  'high' && 'High Attendance (70%+)'}
                                {segmentData.appliedFilters.attendanceRate ===
                                  'medium' && 'Medium Attendance (40-69%)'}
                                {segmentData.appliedFilters.attendanceRate ===
                                  'low' && 'Low Attendance (<40%)'}
                                {!['high', 'medium', 'low'].includes(
                                  segmentData.appliedFilters.attendanceRate
                                ) && segmentData.appliedFilters.attendanceRate}
                              </div>
                            )}
                            {segmentData?.appliedFilters?.locationCategory && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                {segmentData.appliedFilters.locationCategory ===
                                  'local' && 'Local (0-10 miles)'}
                                {segmentData.appliedFilters.locationCategory ===
                                  'nearby' && 'Nearby (11-60 miles)'}
                                {segmentData.appliedFilters.locationCategory ===
                                  'regional' && 'Regional (61-100 miles)'}
                                {segmentData.appliedFilters.locationCategory ===
                                  'distant' && 'Distant (101+ miles, US)'}
                                {segmentData.appliedFilters.locationCategory ===
                                  'international' && 'International'}
                                {![
                                  'local',
                                  'nearby',
                                  'regional',
                                  'distant',
                                  'international',
                                ].includes(
                                  segmentData.appliedFilters.locationCategory
                                ) &&
                                  segmentData.appliedFilters.locationCategory}
                              </div>
                            )}
                            {(segmentData?.appliedFilters?.minValue ||
                              segmentData?.appliedFilters?.maxValue) && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                Spend: $
                                {segmentData.appliedFilters.minValue || 0} - $
                                {segmentData.appliedFilters.maxValue || '∞'}
                              </div>
                            )}
                            {(segmentData?.appliedFilters?.dateFrom ||
                              segmentData?.appliedFilters?.dateTo) && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  if (
                                    segmentData.appliedFilters.dateFrom &&
                                    segmentData.appliedFilters.dateTo
                                  ) {
                                    return `Date: ${new Date(
                                      segmentData.appliedFilters.dateFrom
                                    ).toLocaleDateString()} - ${new Date(
                                      segmentData.appliedFilters.dateTo
                                    ).toLocaleDateString()}`;
                                  } else if (
                                    segmentData.appliedFilters.dateFrom
                                  ) {
                                    return `After ${new Date(
                                      segmentData.appliedFilters.dateFrom
                                    ).toLocaleDateString()}`;
                                  } else {
                                    return `Before ${new Date(
                                      segmentData.appliedFilters.dateTo
                                    ).toLocaleDateString()}`;
                                  }
                                })()}
                              </div>
                            )}
                            {segmentData?.appliedFilters?.segment && (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  background: 'rgba(250, 70, 22, 0.1)',
                                  border: '1px solid rgba(250, 70, 22, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  const segmentMap = {
                                    season_pass: 'Season Pass',
                                    super_fan: 'Super Fan',
                                    regular_fan: 'Regular Fan',
                                    casual_fan: 'Casual Fan',
                                    occasional: 'Occasional',
                                    first_timer: 'First Timer',
                                    inactive: 'Inactive',
                                    frequent: 'Frequent Customers',
                                  };
                                  return (
                                    segmentMap[
                                      segmentData.appliedFilters.segment
                                    ] || segmentData.appliedFilters.segment
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {!segmentData?.isCustomFilter &&
                        !segmentData?.searchTerm && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '16px',
                              gap: '12px',
                            }}
                          >
                            <div
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: 'rgba(250, 70, 22, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                fill={theme?.colors?.primary || '#FA4616'}
                                viewBox="0 0 16 16"
                              >
                                <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                              </svg>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  color: isDarkTheme
                                    ? '#ffffff'
                                    : theme.colors.blueBlack,
                                  marginBottom: '2px',
                                }}
                              >
                                All Customers
                              </div>
                              <div
                                style={{
                                  fontSize: '13px',
                                  color: isDarkTheme
                                    ? 'rgb(148, 163, 184)'
                                    : '#64748B',
                                }}
                              >
                                No filters applied - targeting entire audience
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Message Composer - without preview */}
                  <MessageComposer
                    hidePreview={true}
                    onError={setToasterData}
                    additionalSection={
                      <div
                        style={{
                          background: isDarkTheme
                            ? 'rgba(255, 255, 255, 0.05)'
                            : '#ffffff',
                          borderRadius: '12px',
                          padding: '20px',
                          border: `1px solid ${
                            isDarkTheme
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.08)'
                          }`,
                          boxShadow: isDarkTheme
                            ? 'rgba(0, 0, 0, 0.1) 0px 1px 3px'
                            : 'rgba(0, 0, 0, 0.05) 0px 1px 3px',
                        }}
                      >
                        <SectionTitle>
                          <svg
                            width="16"
                            height="16"
                            fill={theme?.colors?.primary || '#FA4616'}
                            viewBox="0 0 16 16"
                            style={{ marginRight: '8px' }}
                          >
                            <path d="M4 3.06h8a1 1 0 0 1 .78.375l1.25 1.563A1 1 0 0 1 14.25 5.5v6.44a1 1 0 0 1-1 1H2.75a1 1 0 0 1-1-1V5.5a1 1 0 0 1 .22-.5l1.25-1.563A1 1 0 0 1 4 3.06z" />
                            <path
                              fillRule="evenodd"
                              d="M1.75 1.75a.75.75 0 0 0 0 1.5h12.5a.75.75 0 1 0 0-1.5H1.75zM7.5 9V6h1v3a1 1 0 1 1-1 0z"
                            />
                          </svg>
                          Credits Required
                          <div
                            style={{
                              marginLeft: 'auto',
                              fontSize: '12px',
                              color:
                                selectedChannel === 'Push'
                                  ? theme?.colors?.success || '#22C55E'
                                  : theme?.colors?.primary || '#FA4616',
                            }}
                          >
                            {selectedChannel === 'Push'
                              ? 'FREE'
                              : `${creditsNeeded.toLocaleString()} credits`}
                          </div>
                        </SectionTitle>
                        {selectedChannel === 'SMS' && (
                          <div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '12px',
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  height: '8px',
                                  background: isDarkTheme
                                    ? 'rgba(255, 255, 255, 0.2)'
                                    : 'rgba(0, 0, 0, 0.1)',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    height: '100%',
                                    width: `${Math.min(
                                      100,
                                      (creditsUsed / creditLimit) * 100
                                    )}%`,
                                    background: 'rgba(250, 70, 22, 0.5)',
                                    borderRadius: '4px',
                                  }}
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: `${Math.min(
                                      100,
                                      (creditsUsed / creditLimit) * 100
                                    )}%`,
                                    height: '100%',
                                    width: `${Math.min(
                                      100 - (creditsUsed / creditLimit) * 100,
                                      (creditsNeeded / creditLimit) * 100
                                    )}%`,
                                    background:
                                      creditsNeeded > creditBalance
                                        ? 'linear-gradient(90deg, #EF4444, #DC2626)'
                                        : `linear-gradient(90deg, ${
                                            theme?.colors?.primary || '#FA4616'
                                          }, #F97316)`,
                                    transition: 'width 0.3s ease',
                                    borderRadius: '4px',
                                  }}
                                />
                              </div>
                              <button
                                style={{
                                  padding: '4px 10px',
                                  background: 'transparent',
                                  border: `1px solid ${
                                    theme?.colors?.primary || '#FA4616'
                                  }`,
                                  borderRadius: '20px',
                                  color: theme?.colors?.primary || '#FA4616',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background =
                                    'rgba(250, 70, 22, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    'transparent';
                                }}
                                onClick={() => {
                                  // Handle upgrade limit click
                                }}
                              >
                                Upgrade Limit
                              </button>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '11px',
                                color: isDarkTheme
                                  ? 'rgb(148, 163, 184)'
                                  : '#64748B',
                              }}
                            >
                              <div>Credits reset {creditResetDate}</div>
                              <div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color:
                                      creditsNeeded > creditBalance
                                        ? '#EF4444'
                                        : theme?.colors?.primary || '#FA4616',
                                    fontSize: '14px',
                                  }}
                                >
                                  {creditBalance.toLocaleString()}
                                </span>
                                <span> / </span>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: isDarkTheme
                                      ? 'rgb(203, 213, 225)'
                                      : '#94A3B8',
                                  }}
                                >
                                  {creditLimit.toLocaleString()}
                                </span>
                                <span
                                  style={{
                                    color: theme?.colors?.primary || '#FA4616',
                                    fontSize: '10px',
                                    marginLeft: '3px',
                                  }}
                                >
                                  {' '}
                                  available
                                </span>
                              </div>
                            </div>
                            {creditsNeeded > 0 && (
                              <div
                                style={{
                                  marginTop: '12px',
                                  padding: '8px 10px',
                                  background: 'rgba(250, 70, 22, 0.08)',
                                  borderLeft: `3px solid ${
                                    theme?.colors?.primary || '#FA4616'
                                  }`,
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  color: isDarkTheme ? '#cbd5e1' : '#64748B',
                                }}
                              >
                                <strong
                                  style={{
                                    color: theme?.colors?.primary || '#FA4616',
                                  }}
                                >
                                  This message:
                                </strong>{' '}
                                {creditsNeeded.toLocaleString()} credits (
                                {characterData.segments} segment
                                {characterData.segments > 1 ? 's' : ''} ×{' '}
                                {audienceCount.toLocaleString()} customers
                                {characterData.creditMultiplier > 1
                                  ? ` × ${characterData.creditMultiplier}x MMS rate`
                                  : ''}
                                )
                              </div>
                            )}
                            {creditsNeeded > creditBalance && (
                              <div
                                style={{
                                  marginTop: '12px',
                                  padding: '8px 12px',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  color: '#EF4444',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                                </svg>
                                Insufficient credits. You need{' '}
                                {(
                                  creditsNeeded - creditBalance
                                ).toLocaleString()}{' '}
                                more credits.
                              </div>
                            )}
                          </div>
                        )}
                        {selectedChannel === 'Push' && (
                          <div
                            style={{
                              padding: '16px',
                              background: theme?.colors?.success
                                ? `${theme.colors.success}0D`
                                : 'rgba(34, 197, 94, 0.05)',
                              border: theme?.colors?.success
                                ? `1px solid ${theme.colors.success}33`
                                : '1px solid rgba(34, 197, 94, 0.2)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              fill={theme?.colors?.success || '#22C55E'}
                              viewBox="0 0 16 16"
                            >
                              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                            </svg>
                            Push notifications are free - no credits required
                          </div>
                        )}
                      </div>
                    }
                    selectedChannel={selectedChannel}
                    setSelectedChannel={setSelectedChannel}
                    message={message}
                    setMessage={setMessage}
                    pushTitle={pushTitle}
                    setPushTitle={setPushTitle}
                    variables={campaignVariables}
                    replaceVariables={replaceVariables}
                    context="campaign"
                    attachments={attachments}
                    setAttachments={setAttachments}
                    availableChannels={['SMS', 'Push']}
                    onCharacterCountChange={setCharacterData}
                    onInsertVariable={(fn) => {
                      insertVariableAtCursorRef.current = fn;
                    }}
                  />
                </LeftColumn>

                <RightColumn>
                  <MessagePreview
                    channel={selectedChannel}
                    message={replaceVariables(message) || ''}
                    pushTitle={replaceVariables(pushTitle) || ''}
                    showTitle={true}
                    compact={false}
                    attachments={attachments}
                  />
                </RightColumn>
              </ContentGrid>
            </>
          )}

          {currentStep === 3 && (
            <>
              <PageTitle>Review Your Message</PageTitle>

              <StatsBar>
                <StatItem>
                  <span>
                    <svg
                      width="14"
                      height="14"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: '4px' }}
                    >
                      <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.5a1 1 0 0 0-.8.4l-1.9 2.533a1 1 0 0 1-1.6 0L5.3 12.4a1 1 0 0 0-.8-.4H2a2 2 0 0 1-2-2V2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5z" />
                    </svg>
                    CHANNEL
                  </span>
                  <StatValue>{selectedChannel}</StatValue>
                </StatItem>
                <StatItem>
                  <span>
                    <svg
                      width="14"
                      height="14"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: '4px' }}
                    >
                      <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216zM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                    </svg>
                    RECIPIENTS
                  </span>
                  <StatValue>{audienceCount}</StatValue>
                </StatItem>
                <StatItem>
                  <span>
                    <svg
                      width="14"
                      height="14"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: '4px' }}
                    >
                      <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-4 0 1 1 0 0 1-1-1v-7zm1.294 7.456A1.999 1.999 0 0 1 4.732 11h5.536a2.01 2.01 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456zM12 10a1 1 0 0 0 1 1h1.5a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12v4zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                    </svg>
                    CREDITS
                  </span>
                  <StatValue>{creditsNeeded}</StatValue>
                </StatItem>
              </StatsBar>

              <ReviewSection>
                <ReviewLeftColumn>
                  {selectedChannel === 'SMS' && (
                    <DetailCard>
                      <SectionTitle>
                        <svg
                          width="16"
                          height="16"
                          fill={theme?.colors?.primary || '#FA4616'}
                          viewBox="0 0 16 16"
                          style={{ marginRight: '8px' }}
                        >
                          <path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V1zm1 0v5h14V1H1zm0 6v8h14V7H1z" />
                          <path d="M2 4.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z" />
                        </svg>
                        Test Your Message
                      </SectionTitle>

                      <div
                        style={{
                          padding: '16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <p
                          style={{
                            margin: '0 0 16px 0',
                            fontSize: '14px',
                            color: isDarkTheme ? '#CBD5E1' : '#64748B',
                          }}
                        >
                          Send a test message to see exactly what your
                          recipients will receive.
                        </p>

                        <div
                          style={{
                            marginBottom: '16px',
                          }}
                        >
                          <PhoneInput
                            label="Phone Number"
                            value={testPhoneNumber}
                            onChange={setTestPhoneNumber}
                            countryCode={selectedCountry}
                            onCountryChange={(code) => {
                              setSelectedCountry(code);
                              setTestPhoneNumber(''); // Clear phone when country changes
                            }}
                          />
                        </div>

                        <button
                          onClick={handleSendTest}
                          disabled={sendingTest || !message || !testPhoneNumber}
                          className="test-message-button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 24px',
                            background:
                              sendingTest || !message || !testPhoneNumber
                                ? 'transparent'
                                : 'transparent',
                            color:
                              sendingTest || !message || !testPhoneNumber
                                ? '#6B7280'
                                : theme?.colors?.primary || '#FA4616',
                            border:
                              sendingTest || !message || !testPhoneNumber
                                ? '1px solid #374151'
                                : `1px solid ${
                                    theme?.colors?.primary || '#FA4616'
                                  }`,
                            borderRadius: '24px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor:
                              sendingTest || !message || !testPhoneNumber
                                ? 'not-allowed'
                                : 'pointer',
                            transition: 'all 0.2s',
                            transform: 'translateY(0)',
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                          </svg>
                          {sendingTest ? 'Sending...' : 'Send Test Message'}
                        </button>

                        {(!message || !testPhoneNumber) && (
                          <p
                            style={{
                              margin: '12px 0 0 0',
                              fontSize: '12px',
                              color: '#F59E0B',
                            }}
                          >
                            {!message && !testPhoneNumber
                              ? 'Please compose a message and enter a phone number'
                              : !message
                              ? 'Please compose a message first'
                              : 'Please enter a phone number'}
                          </p>
                        )}
                      </div>
                    </DetailCard>
                  )}

                  <DetailCard>
                    <SectionTitle>
                      <svg
                        width="16"
                        height="16"
                        fill={theme?.colors?.primary || '#FA4616'}
                        viewBox="0 0 16 16"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                      </svg>
                      Schedule
                    </SectionTitle>
                    <ScheduleToggle>
                      <ToggleLabel>Schedule for later</ToggleLabel>
                      <ToggleSwitch>
                        <input
                          type="checkbox"
                          checked={scheduleForLater}
                          onChange={(e) => {
                            setScheduleForLater(e.target.checked);
                            if (!e.target.checked) {
                              setScheduledDateTime(null);
                            }
                          }}
                        />
                        <span className="slider"></span>
                      </ToggleSwitch>
                    </ScheduleToggle>
                    {scheduleForLater && (
                      <DateTimePicker
                        value={scheduledDateTime}
                        onChange={(value) => {
                          // Update as selections are made
                          if (
                            value instanceof Date &&
                            !isNaN(value.getTime())
                          ) {
                            setScheduledDateTime(value);
                          }
                        }}
                        onConfirm={(value) => {
                          // Final confirmation
                          if (
                            value instanceof Date &&
                            !isNaN(value.getTime())
                          ) {
                            setScheduledDateTime(value);
                          } else if (value) {
                            // Try to parse it as a Date if it's not already
                            const parsedDate = new Date(value);
                            if (!isNaN(parsedDate.getTime())) {
                              setScheduledDateTime(parsedDate);
                            }
                          }
                        }}
                        showTime={true}
                        confirmText="Set Schedule"
                        minDate={new Date()}
                      />
                    )}
                  </DetailCard>
                </ReviewLeftColumn>

                <ReviewRightColumn>
                  <FinalPreview>
                    <SectionTitle>
                      <svg
                        width="16"
                        height="16"
                        fill={theme?.colors?.primary || '#FA4616'}
                        viewBox="0 0 16 16"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                        <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                      </svg>
                      Final Preview
                    </SectionTitle>
                    <div style={{ marginBottom: '24px' }}>
                      <MessagePreview
                        channel={selectedChannel}
                        message={
                          replaceVariables(message) ||
                          (selectedChannel === 'SMS'
                            ? "Hey John! Don't miss the Sprint Car Championship this Saturday at Thunder Valley. Gates open at 5PM! View details: tkhs.co/info"
                            : "Don't miss the action at Thunder Valley! Gates open at 5PM with special pre-race activities.")
                        }
                        pushTitle={
                          replaceVariables(pushTitle) ||
                          'Sprint Car Championship This Saturday!'
                        }
                        attachments={attachments}
                        showTitle={false}
                        compact={false}
                      />
                    </div>
                  </FinalPreview>

                  <ReadyStatus>
                    <svg
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{
                        color: theme?.colors?.success || '#22C55E',
                        flexShrink: 0,
                      }}
                    >
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                    </svg>
                    <div>
                      <StatusText>
                        {scheduleForLater
                          ? 'Scheduled To Send'
                          : 'Ready to Send'}
                      </StatusText>
                      <p
                        style={{
                          fontSize: '14px',
                          color: isDarkTheme ? '#CBD5E1' : '#64748B',
                          margin: '4px 0 0 0',
                        }}
                      >
                        {scheduleForLater &&
                        scheduledDateTime &&
                        scheduledDateTime instanceof Date &&
                        !isNaN(scheduledDateTime.getTime()) ? (
                          <>
                            Your message will be sent on{' '}
                            <strong
                              style={{
                                color: theme?.colors?.success || '#22C55E',
                              }}
                            >
                              {scheduledDateTime.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </strong>{' '}
                            at{' '}
                            <strong
                              style={{
                                color: theme?.colors?.success || '#22C55E',
                              }}
                            >
                              {scheduledDateTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </strong>
                          </>
                        ) : (
                          <>
                            Your message is optimized and ready to deliver
                            results. It will be sent immediately to maximize
                            engagement.
                          </>
                        )}
                      </p>
                    </div>
                  </ReadyStatus>
                </ReviewRightColumn>
              </ReviewSection>
            </>
          )}
        </PageContent>

        <FooterButtons>
          {currentStep === 2 && (
            <NextButton onClick={handleNext}>
              Review Message
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </NextButton>
          )}
          {currentStep === 3 && (
            <SendButton
              onClick={handleSend}
              disabled={
                isSending ||
                creditsNeeded > creditBalance ||
                audienceCount === 0
              }
            >
              {isSending ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    style={{
                      marginRight: '8px',
                      animation: 'spin 1s linear infinite',
                    }}
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeDasharray="30 10"
                    />
                  </svg>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  Sending...
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                  </svg>
                  {audienceCount === 0
                    ? 'No Recipients'
                    : creditsNeeded > creditBalance
                    ? 'Insufficient Credits'
                    : scheduleForLater
                    ? 'Schedule Message'
                    : 'Send Message'}
                </>
              )}
            </SendButton>
          )}
        </FooterButtons>
      </PageContainer>

      {/* Education Drawer for Campaign Guide */}
      <CampaignGuideDrawer
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />

      {/* Education Drawer for Quick Variables */}
      <QuickVariablesDrawer
        isOpen={showVariableEducation}
        onClose={() => setShowVariableEducation(false)}
      />
      <EventSelectionModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setPendingEventVariable(null);
        }}
        onSelect={(event) => {
          setSelectedEvent(event);
          // Automatically insert the variable they clicked on
          if (
            event &&
            insertVariableAtCursorRef.current &&
            pendingEventVariable
          ) {
            insertVariableAtCursorRef.current(pendingEventVariable);
          }
          setPendingEventVariable(null);
        }}
        selectedEventId={selectedEvent?.id}
      />
      <DiscountSelectionModal
        isOpen={isDiscountModalOpen}
        onClose={() => setIsDiscountModalOpen(false)}
        onSelect={(discount) => {
          setSelectedDiscount(discount);
          // Automatically insert the discount code variable
          if (discount && insertVariableAtCursorRef.current) {
            insertVariableAtCursorRef.current('{{discount_code}}');
          }
        }}
        selectedDiscountCode={selectedDiscount?.name}
      />

      {/* Error Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type || 'error'}
        />
      )}
    </EnhancedThemeProvider>
  );
};

SendCampaign.propTypes = {
  segmentData: PropTypes.shape({
    name: PropTypes.string,
    count: PropTypes.number,
    isCustomFilter: PropTypes.bool,
    appliedFilters: PropTypes.object,
  }),
  onBack: PropTypes.func,
  onSuccess: PropTypes.func,
  accountId: PropTypes.number,
};

export default SendCampaign;
